import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import type { OperatorConfig, OperatorProfile } from '@tennis-scanner/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Per-operator settings — bet sizing, risk policies, profile (display name +
// timezone). updateMyConfig is operatorProcedure (any operator updates their
// OWN row). The full schema is large; this V1 form exposes the highest-impact
// fields. The remaining fields keep their DB defaults until somebody asks.

interface Props {
  profile: OperatorProfile;
  config: OperatorConfig;
}

interface DraftState {
  name: string;
  timezone: string;
  language: string;
  bankrollTotalUsd: string;
  kellyFraction: string;
  kellyFractionDrawdown: string;
  drawdownThresholdPct: string;
  minEdgePct: string;
  maxStakePerBetPct: string;
  minStakeUsd: string;
  edgeMode: 'raw' | 'no_vig' | 'vs_sharp';
  onSharpDisagreement: 'block' | 'reduce_50' | 'warn_only';
}

function fromValues(profile: OperatorProfile, config: OperatorConfig): DraftState {
  return {
    name: profile.name,
    timezone: profile.timezone,
    language: profile.language,
    bankrollTotalUsd: config.bankrollTotalUsd === null ? '' : config.bankrollTotalUsd.toString(),
    kellyFraction: config.kellyFraction.toString(),
    kellyFractionDrawdown: config.kellyFractionDrawdown.toString(),
    drawdownThresholdPct: config.drawdownThresholdPct.toString(),
    minEdgePct: config.minEdgePct.toString(),
    maxStakePerBetPct: config.maxStakePerBetPct.toString(),
    minStakeUsd: config.minStakeUsd.toString(),
    edgeMode: config.edgeMode,
    onSharpDisagreement: config.onSharpDisagreement,
  };
}

export function OperatorConfigForm({ profile, config }: Props) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState<DraftState>(() => fromValues(profile, config));
  useEffect(() => {
    setDraft(fromValues(profile, config));
  }, [profile, config]);

  const update = trpc.operators.updateMyConfig.useMutation({
    onSuccess: async () => {
      toast.success('Config personal actualizada');
      await utils.me.get.invalidate();
    },
    onError: (err) => toast.error('No se pudo guardar', { description: err.message }),
  });

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const bankrollNum =
      draft.bankrollTotalUsd.trim() === '' ? null : Number.parseFloat(draft.bankrollTotalUsd);
    update.mutate({
      name: draft.name.trim(),
      timezone: draft.timezone.trim(),
      language: draft.language.trim(),
      bankrollTotalUsd: bankrollNum,
      kellyFraction: Number.parseFloat(draft.kellyFraction),
      kellyFractionDrawdown: Number.parseFloat(draft.kellyFractionDrawdown),
      drawdownThresholdPct: Number.parseFloat(draft.drawdownThresholdPct),
      minEdgePct: Number.parseFloat(draft.minEdgePct),
      maxStakePerBetPct: Number.parseFloat(draft.maxStakePerBetPct),
      minStakeUsd: Number.parseFloat(draft.minStakeUsd),
      edgeMode: draft.edgeMode,
      onSharpDisagreement: draft.onSharpDisagreement,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id="op-name"
          label="Nombre"
          value={draft.name}
          onChange={(v) => setDraft({ ...draft, name: v })}
        />
        <TextField
          id="op-tz"
          label="Timezone (IANA)"
          value={draft.timezone}
          onChange={(v) => setDraft({ ...draft, timezone: v })}
        />
        <TextField
          id="op-lang"
          label="Lenguaje"
          value={draft.language}
          onChange={(v) => setDraft({ ...draft, language: v })}
        />
        <NumField
          id="op-bankroll"
          label="Bankroll total (USD)"
          step="1"
          value={draft.bankrollTotalUsd}
          onChange={(v) => setDraft({ ...draft, bankrollTotalUsd: v })}
        />
        <NumField
          id="op-kelly"
          label="Kelly fraction (0..1) — ¼-Kelly = 0.25"
          step="0.01"
          value={draft.kellyFraction}
          onChange={(v) => setDraft({ ...draft, kellyFraction: v })}
        />
        <NumField
          id="op-kelly-dd"
          label="Kelly fraction en drawdown (0..1)"
          step="0.01"
          value={draft.kellyFractionDrawdown}
          onChange={(v) => setDraft({ ...draft, kellyFractionDrawdown: v })}
        />
        <NumField
          id="op-dd-threshold"
          label="Drawdown trigger (% del peak)"
          step="0.5"
          value={draft.drawdownThresholdPct}
          onChange={(v) => setDraft({ ...draft, drawdownThresholdPct: v })}
        />
        <NumField
          id="op-edge"
          label="Min edge (0..1)"
          step="0.001"
          value={draft.minEdgePct}
          onChange={(v) => setDraft({ ...draft, minEdgePct: v })}
        />
        <NumField
          id="op-maxstake"
          label="Max stake per bet (% bankroll)"
          step="0.5"
          value={draft.maxStakePerBetPct}
          onChange={(v) => setDraft({ ...draft, maxStakePerBetPct: v })}
        />
        <NumField
          id="op-minstake"
          label="Min stake USD"
          step="1"
          value={draft.minStakeUsd}
          onChange={(v) => setDraft({ ...draft, minStakeUsd: v })}
        />
        <div className="grid gap-1.5">
          <Label htmlFor="op-edgemode">Edge mode</Label>
          <Select
            value={draft.edgeMode}
            onValueChange={(v) => setDraft({ ...draft, edgeMode: v as DraftState['edgeMode'] })}
          >
            <SelectTrigger id="op-edgemode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="raw">raw</SelectItem>
              <SelectItem value="no_vig">no_vig</SelectItem>
              <SelectItem value="vs_sharp">vs_sharp</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="op-sharp">On sharp disagreement</Label>
          <Select
            value={draft.onSharpDisagreement}
            onValueChange={(v) =>
              setDraft({ ...draft, onSharpDisagreement: v as DraftState['onSharpDisagreement'] })
            }
          >
            <SelectTrigger id="op-sharp">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="warn_only">warn_only</SelectItem>
              <SelectItem value="reduce_50">reduce_50</SelectItem>
              <SelectItem value="block">block</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </form>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
}: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function NumField({
  id,
  label,
  step,
  value,
  onChange,
}: {
  id: string;
  label: string;
  step: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
